import { AccessorFn, ColumnDef, DeepKeys } from "@tanstack/react-table";
import { TableCell } from "@/components/ui/table";
import { DateTime } from "luxon";
import { CellHoverClassName } from "@/components/List/ListTable/TableBody/Rows/TableDataRow";

type DateTimeColumnOptions<T> = Omit<ColumnDef<T>, "accessorKey"> & {
	accessorKey?: DeepKeys<T>;
	accessorFn?: AccessorFn<T>;
	format?: string | ((dt: DateTime) => string);
	align?: "left" | "center" | "right";
	className?: string;
	datetimeClassName?: string;
};

export const DateTimeColumn = <T,>({
	accessorKey,
	accessorFn,
	header,
	format = "yyyy-LL-dd HH:mm",
	align = "left",
	className = "",
	datetimeClassName = "",
	meta,
	...rest
}: DateTimeColumnOptions<T>): ColumnDef<T> => {
	const exportFormat = (value: unknown) => {
		if (!value) return "";
		const dt = DateTime.fromISO(String(value));
		return dt.isValid ? (typeof format === "function" ? format(dt) : dt.toFormat(format)) : "Invalid date";
	};

	return {
		accessorKey: accessorKey as string,
		header,
		meta: { wrapWithTableCell: false, exportFormat, cellClassName: className, ...meta },
		cell: ({ getValue }) => {
			const raw = getValue() as string | number | null | undefined;
			const dt = raw ? DateTime.fromISO(String(raw)) : null;

			const formatted = dt?.isValid ? (typeof format === "function" ? format(dt) : dt.toFormat(format)) : "—";

			const alignment = {
				left: "text-left",
				center: "text-center",
				right: "text-right",
			}[align];

			return (
				<TableCell className={`${CellHoverClassName} ${alignment} ${className}`}>
					<div className={`p-2 ${datetimeClassName}`}>{formatted}</div>
				</TableCell>
			);
		},
		...rest,
	};
};
